import { Unit } from "../../Units/UnitConfigurations.js";
import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { UnitCategory } from "../SensorConfigurationTypes.js";
import { SensorElementConfig } from "./types.js";

type SimpleConfig = SensorElementConfig<Record<string, never>, typeof SensorConversions.unity>;

export type SimpleDigitalKind = "count" | "voltage" | "percentage" | "code" | "precipitation" | "state";

type KindSpec = {
    legalConvertedRange: [number | null, number | null];
    units: Unit;
    unitCategory: UnitCategory;
};

const kindSpecs: Record<SimpleDigitalKind, KindSpec> = {
    count: { legalConvertedRange: [0, null], units: "dimensionless", unitCategory: "dimensionless" },
    voltage: { legalConvertedRange: [0, null], units: "volt", unitCategory: "electricalVoltage" },
    percentage: { legalConvertedRange: [0, 1], units: "dimensionless", unitCategory: "arbitraryPercentage" },
    code: { legalConvertedRange: [null, null], units: "dimensionless", unitCategory: "dimensionless" },
    /** Rain since the last reading, not a running total. Buckets add up rather than average. */
    precipitation: { legalConvertedRange: [0, null], units: "millimeter", unitCategory: "precipitation" },
    /**
     * A binary state: 0 for inactive, 1 for active. Distinct from `code` (which is unbounded) and from
     * `percentage` (which has the right range but the wrong unit category) because bounding the range
     * at [0, 1] lets an out-of-range value be caught, and lets the series be averaged over a window to
     * get the fraction of time it was active.
     */
    state: { legalConvertedRange: [0, 1], units: "dimensionless", unitCategory: "dimensionless" },
};

export function makeSimpleDigitalSensorElement(kind: SimpleDigitalKind, sampleValue: number): SimpleConfig {
    const { legalConvertedRange, units, unitCategory } = kindSpecs[kind];
    return {
        legalConvertedRange,
        conversionKey: "unity",
        parameterConfigs: {},
        buildConversionParams: () => ({}) as Record<string, never>,
        decodedUnits: units,
        convertedUnits: units,
        unitCategory,
        testCases: [
            { parameters: {}, decodedValue: 0, convertedValue: 0 },
            { parameters: {}, decodedValue: sampleValue, convertedValue: sampleValue },
        ],
    };
}

export const digitalCountElementConfig = makeSimpleDigitalSensorElement("count", 1);
export const digitalVoltageElementConfig = makeSimpleDigitalSensorElement("voltage", 3.7);
export const digitalPercentageElementConfig = makeSimpleDigitalSensorElement("percentage", 50);
export const digitalCodeElementConfig = makeSimpleDigitalSensorElement("code", 42);
export const digitalPrecipitationElementConfig = makeSimpleDigitalSensorElement("precipitation", 10);
